// src/pages/customer-panel/documents/UserDocumentsPage.js
// Customer Panel - Admin view of a specific user's documents (paginated, with edit/download/view/drive picker)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';
import { useDebouncedCallback } from 'use-debounce';
import useDrivePicker from 'react-google-drive-picker';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, IconButton, Chip, Alert, CircularProgress, Tooltip, Switch, Breadcrumbs, Link as MuiLink,
    LinearProgress, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar,
} from '@mui/material';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TiptapLink from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Color from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import TiptapEditor from '../../../components/Common/TiptapEditor';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import PaginationBar from '../components/PaginationBar';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';
const GREEN_DARK = '#059669';
const GREEN_LIGHT = '#d1fae5';

export default function UserDocumentsPage() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const userRole = user?.active_team?.user_role_in_team || 'customer';
    const isAdmin = userRole === 'admin' || userRole === 'employee';

    // State
    const [userName, setUserName] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // User-uploaded docs
    const [userDocs, setUserDocs] = useState([]);
    const [userPage, setUserPage] = useState(1);
    const [userRowsPerPage, setUserRowsPerPage] = useState(10);
    const [userTotal, setUserTotal] = useState(0);
    const [userSearchInput, setUserSearchInput] = useState('');
    const [userSearch, setUserSearch] = useState('');
    const [userStatus, setUserStatus] = useState('all');

    // Admin-uploaded docs
    const [adminDocs, setAdminDocs] = useState([]);
    const [adminPage, setAdminPage] = useState(1);
    const [adminRowsPerPage, setAdminRowsPerPage] = useState(10);
    const [adminTotal, setAdminTotal] = useState(0);
    const [adminSearchInput, setAdminSearchInput] = useState('');
    const [adminSearch, setAdminSearch] = useState('');
    const [adminStatus, setAdminStatus] = useState('all');

    // Upload
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef(null);

    // Drive picker
    const [selectedDrive, setSelectedDrive] = useState(null);
    const [drivePickerLoading, setDrivePickerLoading] = useState(false);
    const [openGooglePicker] = useDrivePicker();

    // Bulk selection
    const [selectedDocIds, setSelectedDocIds] = useState(new Set());
    const [bulkProcessing, setBulkProcessing] = useState(false);

    // Edit dialog
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingDoc, setEditingDoc] = useState(null);
    const [editContent, setEditContent] = useState('');
    const [editSaving, setEditSaving] = useState(false);
    const [editLoading, setEditLoading] = useState(false);

    // Tiptap editor
    const editEditor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            TiptapLink.configure({ openOnClick: false, autolink: true }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Color,
            TextStyle,
            Highlight.configure({ multicolor: true }),
        ],
        content: '',
    });

    // Debounced search
    const debouncedUserSearch = useDebouncedCallback((val) => { setUserSearch(val); setUserPage(1); }, 500);
    const debouncedAdminSearch = useDebouncedCallback((val) => { setAdminSearch(val); setAdminPage(1); }, 500);

    // Fetch
    const fetchDocuments = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const [userRes, adminRes] = await Promise.all([
                axiosInstance.get('/documents', {
                    params: { user_id: userId, upload_source: 'user', page: userPage, limit: userRowsPerPage, search: userSearch, status: userStatus },
                }),
                axiosInstance.get('/documents', {
                    params: { user_id: userId, upload_source: 'operational', page: adminPage, limit: adminRowsPerPage, search: adminSearch, status: adminStatus },
                }),
            ]);
            setUserDocs(userRes.data?.files || []);
            setUserTotal(userRes.data?.total || 0);
            setAdminDocs(adminRes.data?.files || []);
            setAdminTotal(adminRes.data?.total || 0);
            if (userRes.data?.user_name) setUserName(userRes.data.user_name);
            else if (adminRes.data?.user_name) setUserName(adminRes.data.user_name);
        } catch (err) {
            console.error('Error fetching documents:', err);
            setError('Failed to load documents for this user.');
        } finally { setLoading(false); }
    }, [userId, userPage, userRowsPerPage, userSearch, userStatus, adminPage, adminRowsPerPage, adminSearch, adminStatus]);

    useEffect(() => { if (isAdmin && userId) fetchDocuments(); }, [fetchDocuments]);

    // Init drive setting
    useEffect(() => {
        axiosInstance.get('/settings/drive')
            .then(res => setSelectedDrive(res.data?.selected_drive || null))
            .catch(() => setSelectedDrive(null));
    }, []);

    // Upload
    const handleAdminUpload = async (files) => {
        if (!files || files.length === 0) return;
        setUploading(true); setUploadProgress(0); setError(''); setSuccessMsg('');
        try {
            for (let i = 0; i < files.length; i++) {
                const formData = new FormData();
                formData.append('file', files[i]);
                formData.append('target_user_id', userId);
                setUploadProgress(Math.round((i / files.length) * 90));
                await axiosInstance.post('/documents/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (p) => {
                        const fileProgress = p.loaded / p.total;
                        setUploadProgress(Math.round(((i + fileProgress) / files.length) * 90));
                    },
                });
            }
            setSuccessMsg(`Uploaded ${files.length} document(s) successfully.`);
            fetchDocuments();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to upload document.');
        } finally {
            setUploading(false); setUploadProgress(0);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // Drive picker
    const handleOpenDrivePicker = async () => {
        setDrivePickerLoading(true);
        try {
            if (selectedDrive === 'google_drive') {
                // Use /api/google-drive/status which returns the OAuth access token
                const statusRes = await axiosInstance.get('/api/google-drive/status');
                if (!statusRes.data?.connected || !statusRes.data?.accessToken) {
                    setError('Google Drive not connected. Please connect it in Settings.');
                    return;
                }
                openGooglePicker({
                    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
                    developerKey: process.env.REACT_APP_GOOGLE_API_KEY || '',
                    viewId: 'DOCS',
                    token: statusRes.data.accessToken,
                    showUploadView: true,
                    supportDrives: true,
                    multiselect: true,
                    callbackFunction: (data) => {
                        if (data.action === 'picked') handleFilesPicked(data.docs);
                    },
                });
            } else if (selectedDrive === 'onedrive') {
                setSnackbar({ open: true, message: 'OneDrive picker coming soon.', severity: 'info' });
            }
        } catch (err) {
            setError('Failed to open drive picker.');
        } finally { setDrivePickerLoading(false); }
    };

    const handleFilesPicked = async (pickedFiles) => {
        setUploading(true); setUploadProgress(0); setError('');
        try {
            await axiosInstance.post('/documents/log-picked-files', { files: pickedFiles, target_user_id: userId });
            setSuccessMsg('Files from Drive added successfully!');
            fetchDocuments();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to log picked files.');
        } finally {
            setUploading(false); setUploadProgress(0);
        }
    };

    // Training
    const handleToggleTraining = async (doc) => {
        const newStatus = !doc.isTrained;
        try {
            await axiosInstance.post('/documents/training-status', { file_ids: [doc.id], is_trained: newStatus });
            const updater = (list) => list.map((d) => d.id === doc.id ? { ...d, isTrained: newStatus } : d);
            setUserDocs(updater); setAdminDocs(updater);
        } catch { setError('Failed to update training status.'); }
    };

    const handleBulkTrain = async (trainStatus) => {
        if (selectedDocIds.size === 0) return;
        setBulkProcessing(true);
        try {
            const ids = Array.from(selectedDocIds);
            await axiosInstance.post('/documents/training-status', { file_ids: ids, is_trained: trainStatus });
            const updater = (list) => list.map((d) => selectedDocIds.has(d.id) ? { ...d, isTrained: trainStatus } : d);
            setUserDocs(updater); setAdminDocs(updater);
            setSnackbar({ open: true, message: `${ids.length} document(s) updated.`, severity: 'success' });
            setSelectedDocIds(new Set());
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to update.', severity: 'error' });
        } finally { setBulkProcessing(false); }
    };

    // Selection
    const handleSelectDoc = (doc) => {
        setSelectedDocIds((prev) => { const next = new Set(prev); next.has(doc.id) ? next.delete(doc.id) : next.add(doc.id); return next; });
    };
    const handleSelectAll = (docs) => {
        const ids = docs.map(d => d.id);
        const allSelected = ids.every(id => selectedDocIds.has(id));
        setSelectedDocIds((prev) => { const next = new Set(prev); ids.forEach(id => allSelected ? next.delete(id) : next.add(id)); return next; });
    };

    // Edit
    const handleEditOpen = async (doc) => {
        setEditingDoc(doc); setEditDialogOpen(true); setEditLoading(true);
        if (editEditor) editEditor.commands.setContent('');
        try {
            const res = await axiosInstance.get(`/documents/${doc.id}/content`);
            const content = res.data?.document?.page_content || '';
            setEditContent(content);
            if (editEditor) editEditor.commands.setContent(content);
        } catch {
            setEditContent('');
            if (editEditor) editEditor.commands.setContent('');
        }
        finally { setEditLoading(false); }
    };
    const handleEditSave = async () => {
        if (!editingDoc) return;
        setEditSaving(true);
        try {
            const content = editEditor ? editEditor.getHTML() : editContent;
            await axiosInstance.put(`/documents/${editingDoc.id}/content`, { page_content: content });
            setSnackbar({ open: true, message: 'Document content updated.', severity: 'success' });
            setEditDialogOpen(false); setEditingDoc(null);
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to save.', severity: 'error' });
        } finally { setEditSaving(false); }
    };

    // Download
    const handleDownload = async (doc) => {
        try {
            const res = await axiosInstance.get(`/documents/download/${doc.id}`);
            if (res.data?.download_url) window.open(res.data.download_url, '_blank');
            else setSnackbar({ open: true, message: 'No download link.', severity: 'error' });
        } catch { setSnackbar({ open: true, message: 'Failed to download.', severity: 'error' }); }
    };

    // Helpers
    const formatDate = (d) => {
        if (!d) return '--';
        try { return new Date(d).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }); }
        catch { return '--'; }
    };
    const getFileTypeChip = (type) => {
        const t = (type || '').toLowerCase();
        let label = type || 'File', color = '#64748b', bg = '#f1f5f9';
        if (t.includes('pdf')) { label = 'PDF'; color = '#dc2626'; bg = '#fef2f2'; }
        else if (t.includes('doc') || t.includes('word')) { label = 'DOC'; color = '#2563eb'; bg = '#eff6ff'; }
        else if (t.includes('xls') || t.includes('sheet')) { label = 'XLS'; color = '#16a34a'; bg = '#f0fdf4'; }
        else if (t.includes('csv')) { label = 'CSV'; color = '#059669'; bg = '#ecfdf5'; }
        else if (t.includes('txt') || t.includes('text')) { label = 'TXT'; color = '#64748b'; bg = '#f8fafc'; }
        return <Chip label={label} size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', color, bgcolor: bg, height: 22 }} />;
    };

    // Access check (must be after all hooks)
    if (!isAdmin) {
        return (
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Alert severity="warning" sx={{ fontFamily, borderRadius: '12px' }}>
                    <Typography sx={{ fontFamily, fontWeight: 600 }}>Access Denied</Typography>
                    <Typography variant="body2" sx={{ fontFamily }}>Only administrators can view user-specific documents.</Typography>
                </Alert>
            </Box>
        );
    }

    // Document table
    const renderDocumentTable = (docs, emptyMessage, page, totalRows, rowsPerPage, onPageChange, onRowsPerPageChange, searchInput, onSearchInput, onSearchDebounced, statusFilter, onStatusChange) => {
        const totalPages = Math.ceil(totalRows / rowsPerPage) || 1;
        const tableDocIds = docs.map(d => d.id);
        const allSelected = docs.length > 0 && tableDocIds.every(id => selectedDocIds.has(id));
        const someSelected = tableDocIds.some(id => selectedDocIds.has(id)) && !allSelected;
        const selectedInTable = docs.filter(d => selectedDocIds.has(d.id));

        return (
            <>
                {/* Controls */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                    <TextField size="small" placeholder="Search files..." value={searchInput}
                        onChange={(e) => { onSearchInput(e.target.value); onSearchDebounced(e.target.value); }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 20, color: '#94a3b8' }} /></InputAdornment> }}
                        sx={{ minWidth: 200, '& .MuiOutlinedInput-root': { fontFamily, fontSize: '0.85rem', borderRadius: '10px', bgcolor: '#fff', '& fieldset': { borderColor: '#e2e8f0' }, '&:hover fieldset': { borderColor: GREEN_ACCENT }, '&.Mui-focused fieldset': { borderColor: GREEN_ACCENT } } }}
                    />
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select value={statusFilter} onChange={(e) => onStatusChange(e.target.value)}
                            sx={{ fontFamily, fontSize: '0.85rem', borderRadius: '10px', bgcolor: '#fff' }}>
                            <MenuItem value="all" sx={{ fontFamily, fontSize: '0.85rem' }}>All Status</MenuItem>
                            <MenuItem value="trained" sx={{ fontFamily, fontSize: '0.85rem' }}>Trained</MenuItem>
                            <MenuItem value="untrained" sx={{ fontFamily, fontSize: '0.85rem' }}>Untrained</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {/* Bulk toolbar */}
                {selectedInTable.length > 0 && (
                    <Paper elevation={0} sx={{ mb: 1.5, p: 1.5, px: 2, borderRadius: '12px', bgcolor: '#f0fdf4', border: `1px solid ${GREEN_ACCENT}40`, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontFamily, fontSize: '0.82rem', fontWeight: 600 }}>{selectedInTable.length} selected</Typography>
                        <Button size="small" variant="contained" disabled={bulkProcessing} onClick={() => handleBulkTrain(true)}
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', bgcolor: GREEN_ACCENT, borderRadius: '8px', '&:hover': { bgcolor: GREEN_DARK } }}>
                            Mark Trained
                        </Button>
                        <Button size="small" variant="contained" disabled={bulkProcessing} onClick={() => handleBulkTrain(false)}
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', bgcolor: '#f59e0b', borderRadius: '8px', '&:hover': { bgcolor: '#d97706' } }}>
                            Mark Untrained
                        </Button>
                        <Typography onClick={() => setSelectedDocIds(new Set())} sx={{ fontFamily, fontSize: '0.78rem', color: '#64748b', cursor: 'pointer', ml: 'auto', '&:hover': { textDecoration: 'underline' } }}>Clear</Typography>
                    </Paper>
                )}

                {docs.length === 0 ? (
                    <Paper sx={{ p: 3, textAlign: 'center', borderRadius: '12px', bgcolor: '#f8fafc' }}>
                        <InsertDriveFileIcon sx={{ fontSize: 36, color: '#cbd5e1', mb: 0.5 }} />
                        <Typography variant="body2" sx={{ fontFamily, color: '#94a3b8' }}>{emptyMessage}</Typography>
                    </Paper>
                ) : (
                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    <TableCell padding="checkbox" sx={{ borderBottom: '2px solid #e2e8f0' }}>
                                        <Checkbox size="small" checked={allSelected} indeterminate={someSelected} onChange={() => handleSelectAll(docs)}
                                            sx={{ color: '#94a3b8', '&.Mui-checked': { color: GREEN_ACCENT }, '&.MuiCheckbox-indeterminate': { color: GREEN_ACCENT } }} />
                                    </TableCell>
                                    {['Name', 'Type', 'Status', 'Uploaded', 'Actions'].map((h) => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, color: '#475569', fontSize: '0.78rem', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {docs.map((doc, idx) => {
                                    const isSelected = selectedDocIds.has(doc.id);
                                    return (
                                        <TableRow key={doc.id || idx} selected={isSelected} sx={{ '&:hover': { bgcolor: '#f8fafc' }, ...(isSelected && { bgcolor: '#f0fdf4' }) }}>
                                            <TableCell padding="checkbox">
                                                <Checkbox size="small" checked={isSelected} onChange={() => handleSelectDoc(doc)}
                                                    sx={{ color: '#94a3b8', '&.Mui-checked': { color: GREEN_ACCENT } }} />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <DescriptionIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                                                    <Typography sx={{ fontFamily, fontSize: '0.82rem', fontWeight: 500, color: '#1e293b', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {doc.filename || 'Untitled'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>{getFileTypeChip(doc.fileType || '')}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {doc.isTrained ? (
                                                        <Chip icon={<CheckCircleIcon sx={{ fontSize: 13 }} />} label="Trained" size="small"
                                                            sx={{ fontFamily, fontWeight: 600, fontSize: '0.68rem', bgcolor: GREEN_LIGHT, color: GREEN_DARK, height: 22, '& .MuiChip-icon': { color: GREEN_DARK } }} />
                                                    ) : (
                                                        <Chip icon={<PendingIcon sx={{ fontSize: 13 }} />} label="Untrained" size="small"
                                                            sx={{ fontFamily, fontWeight: 600, fontSize: '0.68rem', bgcolor: '#fef3c7', color: '#92400e', height: 22, '& .MuiChip-icon': { color: '#92400e' } }} />
                                                    )}
                                                    <Tooltip title={doc.isTrained ? 'Disable training' : 'Enable training'}>
                                                        <Switch size="small" checked={!!doc.isTrained} onChange={() => handleToggleTraining(doc)}
                                                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: GREEN_ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: GREEN_ACCENT } }} />
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>{formatDate(doc.upload_date)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Tooltip title="Edit content">
                                                        <IconButton size="small" onClick={() => handleEditOpen(doc)} sx={{ color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {doc.webViewLink && (
                                                        <Tooltip title="View in Drive">
                                                            <IconButton size="small" component="a" href={doc.webViewLink} target="_blank" rel="noopener noreferrer" sx={{ color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}>
                                                                <OpenInNewIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title="Download">
                                                        <IconButton size="small" onClick={() => handleDownload(doc)} sx={{ color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}>
                                                            <DownloadIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <PaginationBar
                    currentPage={page} totalPages={totalPages} totalRows={totalRows}
                    rowsPerPage={rowsPerPage} onPageChange={onPageChange}
                    onRowsPerPageChange={(v) => { onRowsPerPageChange(v); onPageChange(1); }}
                />
            </>
        );
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            {/* Breadcrumb */}
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
                <MuiLink component="button" underline="hover" onClick={() => navigate('/customer-panel/documents')}
                    sx={{ fontFamily, color: '#64748b', fontWeight: 500, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <DescriptionIcon sx={{ fontSize: 16 }} /> Documents
                </MuiLink>
                <Typography sx={{ fontFamily, color: '#1e293b', fontWeight: 600, fontSize: '0.85rem' }}>
                    {loading ? 'Loading...' : userName}
                </Typography>
            </Breadcrumbs>

            {/* Header */}
            <Paper elevation={0} sx={{ p: 2, px: 3, mb: 3, borderRadius: '16px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton onClick={() => navigate('/customer-panel/documents')} sx={{ color: '#fff', mr: 0.5 }}><ArrowBackIcon /></IconButton>
                    <Box>
                        <Typography variant="h6" sx={{ fontFamily, fontWeight: 700 }}>{loading ? 'Loading...' : `${userName}'s Documents`}</Typography>
                        <Typography variant="body2" sx={{ fontFamily, opacity: 0.9 }}>Manage documents for this user</Typography>
                    </Box>
                </Box>
                <Tooltip title="Refresh"><IconButton onClick={fetchDocuments} disabled={loading} sx={{ color: '#fff' }}><RefreshIcon /></IconButton></Tooltip>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2, fontFamily, borderRadius: '10px' }} onClose={() => setError('')}>{error}</Alert>}
            {successMsg && <Alert severity="success" sx={{ mb: 2, fontFamily, borderRadius: '10px' }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: GREEN_ACCENT }} /></Box>
            ) : (
                <>
                    {/* Section 1: User uploaded docs */}
                    <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <PersonIcon sx={{ color: GREEN_ACCENT, fontSize: 22 }} />
                            <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>Documents uploaded by user</Typography>
                            <Chip label={userTotal} size="small" sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_LIGHT, color: GREEN_DARK, height: 22, fontSize: '0.72rem' }} />
                        </Box>
                        {renderDocumentTable(
                            userDocs, 'This user has not uploaded any documents yet.',
                            userPage, userTotal, userRowsPerPage, setUserPage, setUserRowsPerPage,
                            userSearchInput, setUserSearchInput, debouncedUserSearch,
                            userStatus, (v) => { setUserStatus(v); setUserPage(1); }
                        )}
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* Section 2: Admin added docs */}
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AdminPanelSettingsIcon sx={{ color: '#6366f1', fontSize: 22 }} />
                                <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>Documents added by admin</Typography>
                                <Chip label={adminTotal} size="small" sx={{ fontFamily, fontWeight: 600, bgcolor: '#ede9fe', color: '#6366f1', height: 22, fontSize: '0.72rem' }} />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {selectedDrive && (
                                    <Button variant="outlined" startIcon={drivePickerLoading || uploading ? <CircularProgress size={16} sx={{ color: '#3b82f6' }} /> : <FolderOpenIcon />} onClick={handleOpenDrivePicker} disabled={drivePickerLoading || uploading}
                                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', borderColor: '#3b82f6', color: '#3b82f6', borderRadius: '10px', '&:hover': { borderColor: '#2563eb', bgcolor: '#eff6ff' } }}>
                                        {drivePickerLoading ? 'Connecting...' : uploading ? 'Processing...' : 'Select from Drive'}
                                    </Button>
                                )}
                                <Button variant="contained" startIcon={uploading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <CloudUploadIcon />} onClick={() => fileInputRef.current?.click()} disabled={uploading || drivePickerLoading}
                                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_DARK }, borderRadius: '10px', fontSize: '0.82rem' }}>
                                    Add Document
                                </Button>
                                <input type="file" ref={fileInputRef} onChange={(e) => handleAdminUpload(e.target.files)} multiple hidden accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.png,.jpg,.jpeg" />
                            </Box>
                        </Box>

                        {uploading && (
                            <Box sx={{ mb: 2 }}>
                                <LinearProgress variant={uploadProgress === 0 || uploadProgress >= 90 ? 'indeterminate' : 'determinate'} value={uploadProgress}
                                    sx={{ borderRadius: '4px', height: 6, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: GREEN_ACCENT } }} />
                                <Typography variant="caption" sx={{ fontFamily, color: '#64748b', mt: 0.5, display: 'block' }}>
                                    {uploadProgress === 0 ? 'Processing files from Drive...' : uploadProgress >= 90 ? 'Processing on server, please wait...' : `Uploading... ${uploadProgress}%`}
                                </Typography>
                            </Box>
                        )}

                        {renderDocumentTable(
                            adminDocs, 'No documents added by admin for this user yet.',
                            adminPage, adminTotal, adminRowsPerPage, setAdminPage, setAdminRowsPerPage,
                            adminSearchInput, setAdminSearchInput, debouncedAdminSearch,
                            adminStatus, (v) => { setAdminStatus(v); setAdminPage(1); }
                        )}
                    </Box>
                </>
            )}

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onClose={() => { setEditDialogOpen(false); setEditingDoc(null); }} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><EditIcon sx={{ color: GREEN_ACCENT }} />Edit Document Content</Box>
                    <IconButton size="small" onClick={() => { setEditDialogOpen(false); setEditingDoc(null); }}><CloseIcon fontSize="small" /></IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b', mb: 2 }}>
                        Edit content of &quot;{editingDoc?.filename || 'this document'}&quot; for AI training.
                    </Typography>
                    {editLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={32} sx={{ color: GREEN_ACCENT }} /></Box>
                    ) : (
                        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', '&:hover': { borderColor: GREEN_ACCENT }, '&:focus-within': { borderColor: GREEN_ACCENT, boxShadow: `0 0 0 1px ${GREEN_ACCENT}` } }}>
                            <TiptapEditor editor={editEditor} height="350px" />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={() => { setEditDialogOpen(false); setEditingDoc(null); }} disabled={editSaving} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleEditSave} disabled={editSaving || editLoading} variant="contained"
                        startIcon={editSaving ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '10px', bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_DARK } }}>
                        {editSaving ? 'Saving...' : 'Save Content'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: '100%', fontFamily, borderRadius: '10px' }}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}
